import * as moment from "moment-timezone";
import { ReporteChequeo, ReporteRow } from "../models/ReporteChequeo.ts";
import {
  buildVisitRowsForPartnerBranch,
  isSameCalendarDayDayShiftOrdered,
} from "./visitPairingReporteChequeo.ts";
import {
  effectiveNightMetaForPartner,
  STANDARD_SHIFT_CUTOFF_MINUTES,
} from "./nightScheduleEligibility.ts";

/** YYYY-MM-DD from API date string (UTC). */
export function getReportDateKey(checkDateRaw: string): string {
  return moment.utc(checkDateRaw).format("YYYY-MM-DD");
}

export function parseCheckTimeToMinutes(checkTime: string): number {
  const parts = String(checkTime || "00:00:00").split(":");
  const h = Number(parts[0]) || 0;
  const m = Number(parts[1]) || 0;
  const s = Number(parts[2]) || 0;
  return h * 60 + m + s / 60;
}

/** Turno estándar (no nocturno): entrada del día; madrugada &lt;03:00 es salida del día anterior. */
export function isStandardShiftDaytimePunch(
  checkTime: string,
  cutoffMinutes: number = STANDARD_SHIFT_CUTOFF_MINUTES
): boolean {
  return parseCheckTimeToMinutes(checkTime) >= cutoffMinutes;
}

/**
 * Regla simple sin contexto (p. ej. tests): antes del corte → día operativo anterior.
 * Para el reporte real usa {@link operationalDayKeyForReportCheck}.
 */
export function getOperationalDayKey(
  calendarDateKey: string,
  checkTime: string,
  nightScheduleEnabled: boolean,
  cutoffMinutes: number
): string {
  if (!nightScheduleEnabled) return calendarDateKey;
  if (parseCheckTimeToMinutes(checkTime) < cutoffMinutes) {
    return moment
      .utc(calendarDateKey, "YYYY-MM-DD")
      .subtract(1, "day")
      .format("YYYY-MM-DD");
  }
  return calendarDateKey;
}

/**
 * Día operativo para agrupar en reporte con nocturno: la madrugada solo cuenta como
 * cierre del día anterior si hubo checada "de tarde/noche" (>= corte) el día calendario
 * anterior en la misma sucursal. Así una entrada matutina (07:51) no se separa de la
 * salida vespertina (15:14) del mismo día.
 */
/**
 * Día de agrupación en reporte: nocturno → día operativo; normal → calendario
 * (madrugada antes de las 03:00 cuenta como cierre del día anterior).
 */
export function reportGroupDayKeyForCheck(
  r: ReporteChequeo,
  branchMeta: BranchReportMeta,
  allChecks: ReporteChequeo[]
): string {
  const effective = effectiveNightMetaForPartner(
    branchMeta,
    r.candidate_shift_name
  );
  if (effective.nightScheduleEnabled) {
    return operationalDayKeyForReportCheck(r, branchMeta, allChecks);
  }

  const calKey = getReportDateKey(r.assistanceCheck_checkDate);
  const tmin = parseCheckTimeToMinutes(r.assistanceCheck_checkTime);
  if (tmin < STANDARD_SHIFT_CUTOFF_MINUTES) {
    return moment
      .utc(calKey, "YYYY-MM-DD")
      .subtract(1, "day")
      .format("YYYY-MM-DD");
  }
  return calKey;
}

export function operationalDayKeyForReportCheck(
  r: ReporteChequeo,
  meta: BranchReportMeta,
  allChecks: ReporteChequeo[]
): string {
  const calKey = getReportDateKey(r.assistanceCheck_checkDate);
  const tmin = parseCheckTimeToMinutes(r.assistanceCheck_checkTime);
  const effectiveMeta = effectiveNightMetaForPartner(
    meta,
    r.candidate_shift_name
  );
  if (!effectiveMeta.nightScheduleEnabled) return calKey;
  if (tmin >= effectiveMeta.cutoffMinutes) return calKey;

  const branchId = Number(r.branch_id);
  const partnerId = r.partner_id;
  const prevCalKey = moment
    .utc(calKey, "YYYY-MM-DD")
    .subtract(1, "day")
    .format("YYYY-MM-DD");

  const previousDayChecks = allChecks
    .filter(
      (c) =>
        c.partner_id === partnerId &&
        Number(c.branch_id) === branchId &&
        getReportDateKey(c.assistanceCheck_checkDate) === prevCalKey
    )
    .sort((a, b) =>
      a.assistanceCheck_checkTime.localeCompare(b.assistanceCheck_checkTime)
    );

  const currentCalendarDayChecks = allChecks
    .filter(
      (c) =>
        c.partner_id === partnerId &&
        Number(c.branch_id) === branchId &&
        getReportDateKey(c.assistanceCheck_checkDate) === calKey
    )
    .sort((a, b) =>
      a.assistanceCheck_checkTime.localeCompare(b.assistanceCheck_checkTime)
    );

  const morningOnToday = currentCalendarDayChecks.filter(
    (c) =>
      parseCheckTimeToMinutes(c.assistanceCheck_checkTime) <
      effectiveMeta.cutoffMinutes
  );
  const earliestMorning = morningOnToday[0];
  const isEarliestMorningClose =
    earliestMorning != null &&
    Number(earliestMorning.assistanceCheck_id) ===
      Number(r.assistanceCheck_id);

  const hasPriorEveningPunch = hasOpenNightPunchOnPreviousDay(
    previousDayChecks,
    effectiveMeta.cutoffMinutes,
    tmin,
    !effectiveMeta.nightScheduleEnabled
  );

  if (hasPriorEveningPunch && isEarliestMorningClose) {
    return prevCalKey;
  }
  return calKey;
}

export type BranchReportMeta = {
  cutoffMinutes: number;
  nightScheduleEnabled: boolean;
};

/** Fecha de fila en nómina/reporte: día operativo con nocturno, calendario de entrada si no. */
export function operationalDayKeyForVisitRow(
  row: { branch_id?: number; checkIn?: ReporteChequeo },
  branchMeta: Record<number, BranchReportMeta> | undefined,
  allChecks: ReporteChequeo[]
): string {
  const checkIn = row.checkIn;
  if (!checkIn) {
    return "";
  }
  const branchId = Number(row.branch_id ?? checkIn.branch_id);
  const base = branchMeta?.[branchId] ?? defaultMeta();
  return reportGroupDayKeyForCheck(checkIn, base, allChecks);
}

const MAX_GAP_MINUTES_FOR_OPEN_BURST = 15;
const MIN_NIGHT_ENTRY_MINUTES = 18 * 60;
const MIN_GAP_MINUTES_FOR_NEW_VISIT = 10 * 60;

function hasOpenNightPunchOnPreviousDay(
  previousDayChecks: ReporteChequeo[],
  cutoffMinutes: number,
  nextCheckMinutes: number,
  applyDayShiftExclusions = true
): boolean {
  const sortedChecks = [...previousDayChecks].sort((a, b) =>
    a.assistanceCheck_checkTime.localeCompare(b.assistanceCheck_checkTime)
  );

  if (sortedChecks.length === 0) {
    return false;
  }

  if (
    applyDayShiftExclusions &&
    isSameCalendarDayDayShiftOrdered(sortedChecks, cutoffMinutes)
  ) {
    return false;
  }

  let lastSegmentStart = 0;
  for (let i = 0; i < sortedChecks.length - 1; i++) {
    const current = parseCheckTimeToMinutes(sortedChecks[i].assistanceCheck_checkTime);
    const next = parseCheckTimeToMinutes(sortedChecks[i + 1].assistanceCheck_checkTime);
    if (next - current >= MIN_GAP_MINUTES_FOR_NEW_VISIT) {
      lastSegmentStart = i + 1;
    }
  }

  const lastSegment = sortedChecks.slice(lastSegmentStart);
  const last = lastSegment[lastSegment.length - 1];
  const lastMinutes = parseCheckTimeToMinutes(last.assistanceCheck_checkTime);
  const isMorningClose =
    nextCheckMinutes >= 0 && nextCheckMinutes < cutoffMinutes;
  const minOpenStart = isMorningClose
    ? Math.max(cutoffMinutes, 16 * 60)
    : nextCheckMinutes === 0
      ? cutoffMinutes
      : MIN_NIGHT_ENTRY_MINUTES;

  // 14:00/15:00 no debe abrir un turno nocturno hacia una salida de madrugada.
  // 00:00:00 es excepción: puede cerrar una entrada intermedia/tarde del día anterior.
  if (lastMinutes < minOpenStart) {
    return false;
  }

  if (lastSegment.length % 2 === 1) {
    return true;
  }

  let maxSegmentGap = 0;
  for (let i = 0; i < lastSegment.length - 1; i++) {
    const current = parseCheckTimeToMinutes(lastSegment[i].assistanceCheck_checkTime);
    const next = parseCheckTimeToMinutes(lastSegment[i + 1].assistanceCheck_checkTime);
    if (next >= current) {
      maxSegmentGap = Math.max(maxSegmentGap, next - current);
    }
  }

  // Varias marcas seguidas en pocos minutos suelen ser reintentos de una misma entrada.
  return maxSegmentGap <= MAX_GAP_MINUTES_FOR_OPEN_BURST;
}

function sortReporteRowsLikeUtils(rows: ReporteRow[]): ReporteRow[] {
  return [...rows].sort((a, b) => {
    const pa = a.checkIn?.partner_id ?? 0;
    const pb = b.checkIn?.partner_id ?? 0;
    if (pa !== pb) return pa - pb;
    const dateA = a.checkIn?.assistanceCheck_checkDate ?? "";
    const dateB = b.checkIn?.assistanceCheck_checkDate ?? "";
    const dc = dateA.localeCompare(dateB);
    if (dc !== 0) return dc;
    return (a.checkIn?.assistanceCheck_checkTime ?? "").localeCompare(
      b.checkIn?.assistanceCheck_checkTime ?? ""
    );
  });
}

function defaultMeta(): BranchReportMeta {
  return { cutoffMinutes: 3 * 60, nightScheduleEnabled: false };
}

/**
 * Agrupa por (partner, sucursal, día operativo) y delega el pareo a visitPairing.
 */
export function buildReporteChequeoRows(
  records: ReporteChequeo[],
  start: string,
  end: string,
  branchMeta: Record<number, BranchReportMeta>
): ReporteRow[] {
  const input = records || [];
  if (input.length === 0) return [];

  const startDateUTC = moment.utc(start).startOf("day");
  const endDateUTC = moment.utc(end).endOf("day");

  const partnerShiftCache = new Map<number, string | undefined>();

  const groupKeys = new Set<string>();
  for (const r of input) {
    const branchId = Number(r.branch_id);
    const meta = branchMeta[branchId] ?? defaultMeta();
    const opKey = reportGroupDayKeyForCheck(r, meta, input);
    groupKeys.add(`${r.partner_id}|${branchId}|${opKey}`);
    if (!partnerShiftCache.has(r.partner_id)) {
      partnerShiftCache.set(r.partner_id, r.candidate_shift_name);
    }
  }

  const rows: ReporteRow[] = [];

  for (const gk of groupKeys) {
    const [partnerStr, branchStr, opKey] = gk.split("|");
    const partnerId = Number(partnerStr);
    const branchId = Number(branchStr);

    const opDayUTC = moment.utc(opKey, "YYYY-MM-DD");
    if (
      !opDayUTC.isSameOrAfter(startDateUTC, "day") ||
      !opDayUTC.isSameOrBefore(endDateUTC, "day")
    ) {
      continue;
    }

    const shiftName = partnerShiftCache.get(partnerId);
    const meta = effectiveNightMetaForPartner(
      branchMeta[branchId] ?? defaultMeta(),
      shiftName
    );
    const nextCalKey = moment
      .utc(opKey, "YYYY-MM-DD")
      .add(1, "day")
      .format("YYYY-MM-DD");

    const branchBase = branchMeta[branchId] ?? defaultMeta();

    const checkCurrent = input.filter((c) => {
      const currentBranchId = Number(c.branch_id);
      if (c.partner_id !== partnerId || currentBranchId !== branchId) {
        return false;
      }
      if (!meta.nightScheduleEnabled) {
        return (
          getReportDateKey(c.assistanceCheck_checkDate) === opKey &&
          isStandardShiftDaytimePunch(
            c.assistanceCheck_checkTime,
            meta.cutoffMinutes
          )
        );
      }
      return (
        getReportDateKey(c.assistanceCheck_checkDate) === opKey &&
        operationalDayKeyForReportCheck(c, branchBase, input) === opKey
      );
    });
    const checkNext = input.filter((c) => {
      const currentBranchId = Number(c.branch_id);
      if (c.partner_id !== partnerId || currentBranchId !== branchId) {
        return false;
      }
      if (getReportDateKey(c.assistanceCheck_checkDate) !== nextCalKey) {
        return false;
      }
      if (
        parseCheckTimeToMinutes(c.assistanceCheck_checkTime) >=
        meta.cutoffMinutes
      ) {
        return false;
      }
      if (!meta.nightScheduleEnabled) {
        return true;
      }
      return operationalDayKeyForReportCheck(c, branchBase, input) === opKey;
    });

    const allCurrent = input.filter((c) => {
      if (c.partner_id !== partnerId) {
        return false;
      }
      if (getReportDateKey(c.assistanceCheck_checkDate) !== opKey) {
        return false;
      }
      if (!meta.nightScheduleEnabled) {
        return isStandardShiftDaytimePunch(
          c.assistanceCheck_checkTime,
          meta.cutoffMinutes
        );
      }
      return (
        operationalDayKeyForReportCheck(c, branchBase, input) === opKey
      );
    });
    const allNext = input.filter((c) => {
      if (c.partner_id !== partnerId) {
        return false;
      }
      if (getReportDateKey(c.assistanceCheck_checkDate) !== nextCalKey) {
        return false;
      }
      if (
        parseCheckTimeToMinutes(c.assistanceCheck_checkTime) >=
        meta.cutoffMinutes
      ) {
        return false;
      }
      if (!meta.nightScheduleEnabled) {
        return true;
      }
      return operationalDayKeyForReportCheck(c, branchBase, input) === opKey;
    });

    const visitRows = buildVisitRowsForPartnerBranch(
      checkCurrent.sort((a, b) =>
        a.assistanceCheck_checkTime.localeCompare(b.assistanceCheck_checkTime)
      ),
      checkNext.sort((a, b) =>
        a.assistanceCheck_checkTime.localeCompare(b.assistanceCheck_checkTime)
      ),
      branchId,
      partnerId,
      allCurrent,
      allNext,
      meta.cutoffMinutes,
      meta.nightScheduleEnabled
    );
    rows.push(...visitRows);
  }

  return sortReporteRowsLikeUtils(rows);
}
